/**
 * Chart.js configuration for ScoreHistoryChart.
 * Registration, options, and gradient factory.
 */
import {
  Chart as ChartJS,
  ChartOptions,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip
} from "chart.js";

import { color, fontFamily } from "style/constants";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip
);

export const Y_MAX = 1200;

export const chartOptions: ChartOptions<"line"> = {
  responsive: true,
  maintainAspectRatio: true,
  layout: {
    padding: { left: 10, right: 10 }
  },
  plugins: {
    tooltip: {
      backgroundColor: color.SCORE_CHART_TOOLTIP,
      borderColor: color.SCORE_CHART_LINE,
      borderWidth: 1,
      bodyColor: color.NETURALS900,
      bodyFont: { size: 12 },
      cornerRadius: 5,
      caretSize: 0,
      caretPadding: 12,
      displayColors: false,
      padding: { top: 2, bottom: 2, left: 8, right: 8 },
      yAlign: "bottom",
      xAlign: "center",
      callbacks: {
        title: () => "",
        label: ctx => String(Math.round(ctx.parsed.y))
      }
    }
  },
  elements: {
    point: {
      radius: 4,
      backgroundColor: color.SCORE_CHART_LINE
    },
    line: {
      borderWidth: 3,
      borderColor: color.SCORE_CHART_LINE,
      tension: 0
    }
  },
  scales: {
    x: {
      offset: true,
      grid: {
        display: false,
        drawBorder: true,
        borderColor: color.SCORE_CHART_GRID,
        borderWidth: 1,
        z: 1
      },
      ticks: {
        font: {
          family: fontFamily.primary,
          size: 12,
          weight: "300"
        },
        color: color.DARKGRAY
      }
    },
    y: {
      min: 0,
      max: Y_MAX,
      grid: {
        color: color.SCORE_CHART_GRID,
        borderDash: [5.25, 5.25],
        drawBorder: false
      },
      ticks: {
        stepSize: 600,
        font: {
          family: fontFamily.primary,
          size: 12,
          weight: "300"
        },
        color: color.DARKGRAY,
        callback: value => String(value)
      }
    }
  }
};

/**
 * Builds the gradient fill for the area under the line.
 */
export const createAreaGradient = (
  ctx: CanvasRenderingContext2D,
  chartArea: { top: number; bottom: number }
) => {
  const gradient = ctx.createLinearGradient(
    0,
    chartArea.top,
    0,
    chartArea.bottom
  );
  gradient.addColorStop(0, color.SCORE_CHART_LINE);
  gradient.addColorStop(1, color.WHITE);
  return gradient;
};
